import {
  Button,
  ButtonVariant,
  CheckBox,
  CheckboxAlignment,
  FormMessage,
  FormMessageVariant,
  Input,
  InputGroup,
  type SelectOption,
  toast,
  ToggleSwitch,
} from '@aus-platform/design-system';
import { useAppSelector } from 'app/hooks';
import { capitalize, isEmpty, isNil } from 'lodash';
import {
  selectMap3dDataset,
  selectMap3DState,
} from 'map-3d/shared/map-3d-slices';
import { useEffect, useMemo } from 'react';
import { handleResponseSuccessMessage, useIteration } from 'shared/api';
import {
  type GenerateDTMRequestPayload,
  useGenerateDTMRequest,
} from 'shared/api/workspace';
import { Placeholders } from 'shared/constants';
import { TerrainGenerationMessage } from 'shared/enums';
import { Formatter } from 'shared/helpers';
import { useInputFields } from 'shared/hooks';
import { type WorkspaceLayer, zoomToLayer } from '../..';
import { SourceType, SurfaceObject } from './enums';
import {
  type GenerateDTMFormType,
  type GenerateDTMInputType,
  type PointCloudWorkspaceLayer,
} from './types';

export const GenerateDTMForm: React.FC<GenerateDTMFormType> = ({
  visiblePointCloudLayers,
  setIsGenerateDTMFormSubmitted,
}) => {
  // Selectors.
  const { selectedTerrainIteration, selectedTerrainSite } =
    useAppSelector(selectMap3dDataset);
  const { cesiumProxy } = useAppSelector(selectMap3DState);

  // useMemos.
  const pointCloudLayerOptions = useMemo(
    () =>
      Object.values(visiblePointCloudLayers).map((layer: WorkspaceLayer) => ({
        label: layer.name,
        value: {
          ...layer,
          tooltipText: layer.canManageLayer
            ? null
            : `You don't have create & manage permissions for the parent site of ${layer.name}`,
        },
      })),
    [visiblePointCloudLayers],
  );

  // Constants.
  const generateDTMInitialState: GenerateDTMInputType = isEmpty(
    pointCloudLayerOptions,
  )
    ? {
        sourceType: SourceType.SurfaceModel,
        pointCloudLayer: null,
        iteration: selectedTerrainIteration?.id ?? null,
        surfaceObjects: [],
        otherObjectsText: '',
      }
    : {
        sourceType: SourceType.PointCloud,
        pointCloudLayer:
          pointCloudLayerOptions.find(
            (pointCloudLayerOption) =>
              pointCloudLayerOption.value.canManageLayer,
          ) ?? null,
        iteration: null,
        surfaceObjects: [],
        otherObjectsText: '',
      };

  // Hooks.
  const { names, values, errors, setValues, onChange, onBlur, onFocus } =
    useInputFields<GenerateDTMInputType>(generateDTMInitialState);

  // Variables.
  const {
    sourceType,
    surfaceObjects,
    pointCloudLayer,
    iteration,
    otherObjectsText,
  } = values;

  const surfaceObjectOptions = Object.values(SurfaceObject);

  const selectedCount = surfaceObjects.length;

  const isParentCheckboxChecked = selectedCount === surfaceObjectOptions.length;

  const isParentCheckboxIndeterminate =
    selectedCount > 0 && selectedCount < surfaceObjectOptions.length;

  const isPointCloudSource = sourceType === SourceType.PointCloud;

  const canManageIteration =
    selectedTerrainIteration?.canManageIterations ?? false;

  const canManageLayer = pointCloudLayer?.value.canManageLayer ?? false;

  const hasGenerateDTMAccess = canManageIteration || canManageLayer;

  const siteName = hasGenerateDTMAccess
    ? isPointCloudSource
      ? pointCloudLayer?.value.site.name
      : selectedTerrainSite?.name
    : null;

  const iterationName = hasGenerateDTMAccess
    ? isPointCloudSource
      ? pointCloudLayer?.value.iteration.name
      : selectedTerrainIteration?.name
    : null;

  const hasIterationInfo = !isNil(siteName) && !isNil(iterationName);

  const isOtherSurfaceObjectSelected = surfaceObjects.includes(
    SurfaceObject.Other,
  );

  const isGenerateDTMDisabled =
    (isPointCloudSource && isNil(pointCloudLayer)) ||
    (!isPointCloudSource && isNil(iteration)) ||
    isEmpty(surfaceObjects) ||
    (isOtherSurfaceObjectSelected && isEmpty(otherObjectsText));

  const selectedLayerIterationId =
    pointCloudLayer?.value?.iteration?.id ?? null;

  // API.
  const {
    mutate: submitGenerateDTMRequest,
    data: generateDTMResponse,
    isPending: isGenerateDTMLoading,
    isError: isGenerateDTMError,
    isSuccess: isGenerateDTMSuccess,
  } = useGenerateDTMRequest();

  const { data: iterationResponse } = useIteration(
    selectedLayerIterationId ?? '',
    false,
    !isNil(selectedLayerIterationId),
  );

  const selectedLayerHasCapturedDEM =
    !isNil(iterationResponse?.data.capturedDSMCog?.id) ||
    !isNil(iterationResponse?.data.capturedDSM?.id);

  let formMessage = TerrainGenerationMessage.NoActiveTerrain;
  let formVariant = FormMessageVariant.Info;

  if (isPointCloudSource) {
    if (selectedLayerHasCapturedDEM) {
      formMessage = TerrainGenerationMessage.HasCapturedDTM;
      formVariant = FormMessageVariant.Warning;
    } else {
      formMessage = TerrainGenerationMessage.NoCapturedDTM;
      formVariant = FormMessageVariant.Info;
    }
  } else if (!isNil(selectedTerrainIteration?.id)) {
    formMessage = TerrainGenerationMessage.HasActiveTerrain;
    formVariant = FormMessageVariant.Warning;
  }

  // useEffects.
  useEffect(() => {
    if (isNil(cesiumProxy) || isNil(pointCloudLayer) || !canManageLayer) {
      return;
    }

    zoomToLayer(pointCloudLayer.value, cesiumProxy, pointCloudLayer.value.site);
  }, [pointCloudLayer?.value?.id, cesiumProxy]);

  useEffect(() => {
    if (!isNil(selectedTerrainIteration?.id)) {
      setValues((values) => ({
        ...values,
        iteration: selectedTerrainIteration.id,
      }));
    }
  }, [selectedTerrainIteration]);

  useEffect(() => {
    if (isGenerateDTMSuccess) {
      setIsGenerateDTMFormSubmitted(true);
      handleResponseSuccessMessage(isGenerateDTMSuccess, generateDTMResponse);
    }

    if (isGenerateDTMError) {
      toast.error('Failed to submit DTM request. Please try again.');
    }
  }, [isGenerateDTMSuccess, isGenerateDTMError]);

  // Handlers.
  const onToggleChange = () => {
    setValues((values) => ({
      ...values,
      sourceType: isPointCloudSource
        ? SourceType.SurfaceModel
        : SourceType.PointCloud,
    }));
  };

  const onChangeLayer = (
    selectedOption: SelectOption<PointCloudWorkspaceLayer>,
  ) => {
    setValues((values) => ({
      ...values,
      pointCloudLayer: selectedOption,
    }));
  };

  const onClickParentCheckbox = () => {
    setValues((values) => ({
      ...values,
      surfaceObjects:
        values.surfaceObjects.length === surfaceObjectOptions.length
          ? []
          : [...surfaceObjectOptions],
    }));
  };

  const onClickCheckbox = (surfaceObject: SurfaceObject) => {
    setValues((values) => {
      const isSelected = values.surfaceObjects.includes(surfaceObject);

      let updatedSurfaceObjects = isSelected
        ? values.surfaceObjects.filter((object) => object !== surfaceObject)
        : [...values.surfaceObjects, surfaceObject];

      // Ensure "Other" is always the last item if selected.
      if (updatedSurfaceObjects.includes(SurfaceObject.Other)) {
        updatedSurfaceObjects = [
          ...updatedSurfaceObjects.filter(
            (object) => object !== SurfaceObject.Other,
          ),
          SurfaceObject.Other,
        ];
      }

      return {
        ...values,
        surfaceObjects: updatedSurfaceObjects,
      };
    });
  };

  const onSubmitGenerateRequest = (event: React.FormEvent) => {
    event.preventDefault();

    const generateDTMPayload: GenerateDTMRequestPayload = {
      sourceType,
      surfaceObjects,
    };

    if (isPointCloudSource && !isNil(pointCloudLayer?.value.id)) {
      generateDTMPayload.pointCloudLayer = pointCloudLayer.value.id;
    }

    if (!isPointCloudSource && !isNil(iteration)) {
      generateDTMPayload.iteration = iteration;
    }

    if (isOtherSurfaceObjectSelected) {
      generateDTMPayload.otherObjectsText = otherObjectsText.trim();
    }

    submitGenerateDTMRequest(generateDTMPayload);
  };

  return (
    <form className="generate-dtm__form" onSubmit={onSubmitGenerateRequest}>
      <div className="generate-dtm__form-container">
        <InputGroup className="input-field--bold">
          <Input.Label>Source Dataset</Input.Label>

          <ToggleSwitch
            leftTitle={Formatter.toTitleCase(SourceType.PointCloud, '_')}
            rightTitle={Formatter.toTitleCase(SourceType.SurfaceModel, '_')}
            leftSelected={isPointCloudSource}
            onChange={onToggleChange}
            disabled={isGenerateDTMLoading}
            key={sourceType}
          />
        </InputGroup>

        <InputGroup>
          <Input.Label>
            {isPointCloudSource ? 'Select Layer' : 'Active Terrain'}
          </Input.Label>

          {isPointCloudSource ? (
            <Input.Select
              placeholder={Placeholders.SelectLayerFromWorkspace}
              options={pointCloudLayerOptions}
              name={names.pointCloudLayer}
              value={pointCloudLayer}
              isDisabled={
                isEmpty(pointCloudLayerOptions) ||
                isGenerateDTMLoading ||
                !canManageLayer
              }
              isOptionDisabled={(option) => !option.value.canManageLayer}
              onChange={onChangeLayer}
            />
          ) : (
            <Input.Text
              placeholder="No Terrain Selected"
              value={selectedTerrainIteration?.name ?? ''}
              disabled
            />
          )}
        </InputGroup>

        <div className="generate-dtm__form__context">
          {hasIterationInfo ? (
            <>
              <div className="generate-dtm__form__context-item">
                <span className="generate-dtm__form__context-label">Site:</span>

                <span className="generate-dtm__form__context-value">
                  {siteName}
                </span>
              </div>
              <div className="generate-dtm__form__context-item">
                <span className="generate-dtm__form__context-label">
                  Iteration:
                </span>

                <span className="generate-dtm__form__context-value">
                  {iterationName}
                </span>
              </div>
            </>
          ) : (
            <FormMessage
              variant={FormMessageVariant.Error}
              message={
                isPointCloudSource
                  ? TerrainGenerationMessage.NoPointCloudLayerError
                  : TerrainGenerationMessage.NoActiveTerrainError
              }
            />
          )}
        </div>

        <InputGroup className="generate-dtm__form__surface-objects">
          <div className="generate-dtm__form__parent-checkbox">
            <Input.Label isRequired>Objects to Remove</Input.Label>

            <Input.CheckBox
              checked={isParentCheckboxChecked}
              indeterminate={isParentCheckboxIndeterminate}
              onClick={onClickParentCheckbox}
              disabled={isGenerateDTMLoading}
            />
          </div>

          {surfaceObjectOptions.map((surfaceObjectOption: SurfaceObject) => (
            <CheckBox
              key={surfaceObjectOption}
              title={capitalize(surfaceObjectOption)}
              checked={surfaceObjects.includes(surfaceObjectOption)}
              alignCheckbox={CheckboxAlignment.Right}
              onClick={() => onClickCheckbox(surfaceObjectOption)}
              disabled={isGenerateDTMLoading}
              showCard
            />
          ))}

          {isOtherSurfaceObjectSelected && (
            <div className="generate-dtm__form__textarea">
              <Input.TextArea
                placeholder="Enter other non-ground objects to remove (e.g., stockpiles, powerlines, or type “everything” to remove all non-ground objects)"
                name={names.otherObjectsText}
                error={errors.otherObjectsText}
                value={otherObjectsText}
                isInvalid={!!errors.otherObjectsText}
                disabled={isGenerateDTMLoading}
                {...{ onChange, onBlur, onFocus }}
              />
            </div>
          )}
        </InputGroup>

        <FormMessage
          className="generate-dtm__form-message"
          message={formMessage}
          variant={formVariant}
        />
      </div>

      <div className="generate-dtm__form__footer">
        <Button
          variant={ButtonVariant.Primary}
          type="submit"
          disabled={isGenerateDTMDisabled}
          isLoading={isGenerateDTMLoading}
        >
          {isGenerateDTMLoading ? 'Generating Request' : 'Generate Request'}
        </Button>
      </div>
    </form>
  );
};
